// src/pages/DailyProfitPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiGet } from "../utils/apiClient";
import { useUserContext } from "../contexts/UserContext";
import { useMallContext } from "../contexts/MallContext";
import dayjs from "dayjs";
import "./TableCommon.css";
import "./DailyProfitPage.css";
import LoadingInline from "../components/LoadingInline";
import { downloadCsv, type CsvCol } from "../utils/csvExport";
import { isFreePlanUser } from "../utils/plan";
import { UPGRADE_FORM_URL, notifyUpgradeInterest } from "../components/FreePlanUpgradeDialog";
import PromotionCostBreakdownYoYChart, {
  type PromotionCostBreakdownYoYRow,
} from "../components/PromotionCostBreakdownYoYChart";

import {
  COLUMN_DEFS,
  HEADER_STRUCTURE,
  type DailyRow,
  type HeaderSectionDef,
} from "./DailyProfitColumns";

// -------------------------------
// フォーマット
// -------------------------------
const fmtNum = (v?: number | null) =>
  v === null || v === undefined ? "" : Math.round(v).toLocaleString("ja-JP");

const fmtRate = (v?: number | null) =>
  v === null || v === undefined ? "" : (v * 100).toFixed(1) + "%";

type MetricKey = (typeof COLUMN_DEFS)[number]["key"];

// 物流費はRMS注文データ未取込の日にBQがNULLを返す（本当の0と区別）→「ー」表示
const LOGISTICS_KEYS = new Set<MetricKey>([
  "logistics_cost",
  "logistics_cost_rate",
]);

const fmtCell = (
  key: MetricKey,
  type: "num" | "rate",
  v?: number | null
) => {
  if (LOGISTICS_KEYS.has(key) && v === null) return "ー";
  return type === "rate" ? fmtRate(v) : fmtNum(v);
};

const RAKUTEN_ONLY_KEYS = new Set<MetricKey>([
  "ec_amount_spent",
  "ec_amount_spent_rate",
  "rpp_amount_spent",
  "rpp_amount_spent_rate",
  "rppexp_amount_spent",
  "rppexp_amount_spent_rate",
  "cpa_amount_spent",
  "cpa_amount_spent_rate",
  "cpnadv_amount_spent",
  "cpnadv_amount_spent_rate",
  "tda_amount_spent",
  "tda_amount_spent_rate",
  "tdaexp_amount_spent",
  "tdaexp_amount_spent_rate",
  "deal_amount_spent",
  "deal_amount_spent_rate",
  // 楽天外広告費は rms_monthly_costs（楽天用テーブル）にしか値が無いため楽天専用
  "non_rakuten_ad_1_cost",
  "non_rakuten_ad_1_cost_rate",
  "non_rakuten_ad_2_cost",
  "non_rakuten_ad_2_cost_rate",
  "non_rakuten_ad_3_cost",
  "non_rakuten_ad_3_cost_rate",
]);

// Amazon ではスポンサー広告の内訳が rpp/rppexp/cpnadv のキーに入っている
// （API側で ads_sp/sb/sd をマップ）。楽天専用扱いで隠さず、Amazon 名称で表示する
const AMAZON_AD_BREAKDOWN_TITLES: Partial<Record<MetricKey, string>> = {
  cpnadv_amount_spent: "スポンサーディスプレイ",
  rpp_amount_spent: "スポンサープロダクト",
  rppexp_amount_spent: "スポンサーブランド",
};
const AMAZON_VISIBLE_AD_KEYS = new Set<MetricKey>([
  "rpp_amount_spent",
  "rpp_amount_spent_rate",
  "rppexp_amount_spent",
  "rppexp_amount_spent_rate",
  "cpnadv_amount_spent",
  "cpnadv_amount_spent_rate",
]);

// sub を持つセクションか？
function hasSub(
  sec: HeaderSectionDef
): sec is Extract<
  HeaderSectionDef,
  { sub: readonly { name: string; groups: readonly string[] }[] }
> {
  return "sub" in sec;
}

// rowSpan2 セクションか？
function isRowSpan2(
  sec: HeaderSectionDef
): sec is Extract<HeaderSectionDef, { rowSpan2?: true }> {
  return "rowSpan2" in sec && sec.rowSpan2 === true;
}

// ✅ 数値がマイナスか（rate/num共通で判定）
function isNegative(v: unknown): boolean {
  return typeof v === "number" && Number.isFinite(v) && v < 0;
}

const STORAGE_KEY = "dailyProfit.visibleColumns.v1";
// 販促費内訳グラフの開閉（初期は閉じる。開いた状態をブラウザに記憶）
const PROMO_CHART_OPEN_KEY = "dailyProfit.promoChartOpen.v1";
const WEEKDAY_JP = ["日", "月", "火", "水", "木", "金", "土"];

// 楽天イベント（全店共通マスタ。superの「楽天イベント管理」で登録）
type RakutenEvent = {
  id: string;
  type: string; // SS | MA | OTHER
  label: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
};

// バッジ表示の優先順: スーパーSALE > マラソン > その他（> 5と0のつく日）
const EVENT_PRIORITY: Record<string, number> = { SS: 3, MA: 2, OTHER: 1 };

const DailyProfitPage: React.FC = () => {
  const user = useUserContext();
  const { mall } = useMallContext();
  const isAmazonMall = mall === "amazon";
  const isYahooMall = mall === "yahoo";
  const isRakutenMall = mall !== "amazon" && mall !== "yahoo";

  const [rows, setRows] = useState<DailyRow[]>([]);
  const [totalRow, setTotalRow] = useState<DailyRow | null>(null);

  // 営業利益ハイライト：上位3日（黒字のみ）/ 下位3日（赤字のみ）の日付セット
  const opHighlight = useMemo(() => {
    const vals = rows
      .filter(
        (r) =>
          typeof r.operating_profit === "number" &&
          Number.isFinite(r.operating_profit)
      )
      .map((r) => ({
        date: r.report_date ?? "",
        v: r.operating_profit as number,
      }))
      .sort((a, b) => b.v - a.v);
    const good = new Set(
      vals.slice(0, 3).filter((x) => x.v > 0).map((x) => x.date)
    );
    const bad = new Set(
      vals.slice(-3).filter((x) => x.v < 0).map((x) => x.date)
    );
    return { good, bad };
  }, [rows]);
  const [loading, setLoading] = useState(true);

  // 列の表示 / 非表示設定
  const [visibleCols, setVisibleCols] = useState<Record<string, boolean>>({});

  // 初期表示月の決定
  const initMonth = (() => {
    const today = dayjs();
    return today.date() === 1
      ? today.subtract(1, "month").format("YYYY-MM")
      : today.format("YYYY-MM");
  })();
  const [targetMonth, setTargetMonth] = useState(initMonth);

  // 楽天イベント（表示専用。取得失敗時はバッジが出ないだけ）
  const [rakutenEvents, setRakutenEvents] = useState<RakutenEvent[]>([]);

  useEffect(() => {
    if (!isRakutenMall) {
      setRakutenEvents([]);
      return;
    }
    let aborted = false;
    (async () => {
      try {
        const res = await apiGet(`/api/rakuten-events?month=${targetMonth}`);
        if (!res.ok) return;
        const json = await res.json();
        if (!aborted) {
          setRakutenEvents(Array.isArray(json.events) ? json.events : []);
        }
      } catch {
        // バッジ表示のみなので失敗は無視
      }
    })();
    return () => {
      aborted = true;
    };
  }, [isRakutenMall, targetMonth]);

  const eventOfDate = React.useCallback(
    (dateStr: string): RakutenEvent | null => {
      if (!rakutenEvents.length) return null;
      const d = dayjs(dateStr, "YYYY/M/D", true);
      if (!d.isValid()) return null;
      const key = d.format("YYYY-MM-DD");
      let best: RakutenEvent | null = null;
      for (const e of rakutenEvents) {
        if (e.startDate <= key && key <= e.endDate) {
          if (
            !best ||
            (EVENT_PRIORITY[e.type] ?? 0) > (EVENT_PRIORITY[best.type] ?? 0)
          ) {
            best = e;
          }
        }
      }
      return best;
    },
    [rakutenEvents]
  );

  // =========================
  // ★ Free plan: 月切り替えブロック
  // =========================
  const isFreePlan = useMemo(() => isFreePlanUser(user), [user]);

  const [planBlockMsg, setPlanBlockMsg] = useState<string | null>(null);
  const planBlockTimer = React.useRef<number | null>(null);

  const showPlanBlocked = React.useCallback((msg?: string) => {
    setPlanBlockMsg(msg || "フリープランではこの年月は閲覧できません。");
    if (planBlockTimer.current) window.clearTimeout(planBlockTimer.current);
    planBlockTimer.current = window.setTimeout(() => setPlanBlockMsg(null), 2500);
  }, []);

  useEffect(() => {
    return () => {
      if (planBlockTimer.current) window.clearTimeout(planBlockTimer.current);
    };
  }, []);

  // =========================
  // ★ Free plan: 直近7日だけ閲覧（それ以外はブラー）
  // =========================
  const FREE_VISIBLE_DAYS = 7;

  const freeWindow = useMemo(() => {
    if (!isFreePlan) return null;
    const end = dayjs().startOf("day");
    const start = end.subtract(FREE_VISIBLE_DAYS - 1, "day").startOf("day");
    return { start, end };
  }, [isFreePlan]);

  const isFreeRowAllowed = React.useCallback(
    (r: DailyRow) => {
      if (!isFreePlan) return true;
      if (!freeWindow) return true;

      const d = dayjs(r.report_date, "YYYY/M/D", true);
      if (!d.isValid()) return false;

      const ts = d.startOf("day").valueOf();
      return ts >= freeWindow.start.valueOf() && ts <= freeWindow.end.valueOf();
    },
    [isFreePlan, freeWindow]
  );

  // ✅ フリープランで「選択できる月」判定
  const isFreeMonthAllowed = React.useCallback(
    (ym: string) => {
      if (!isFreePlan) return true;
      if (!freeWindow) return false;

      const startYm = freeWindow.start.format("YYYY-MM");
      const endYm = freeWindow.end.format("YYYY-MM");
      return ym === startYm || ym === endYm;
    },
    [isFreePlan, freeWindow]
  );

  // =========================
  // 販促費内訳グラフ（ダッシュボードと同じ部品・1日1本）
  // =========================
  const [promoChartOpen, setPromoChartOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(PROMO_CHART_OPEN_KEY) === "1";
    } catch {
      return false;
    }
  });

  const onTogglePromoChart = (open: boolean) => {
    setPromoChartOpen(open);
    try {
      localStorage.setItem(PROMO_CHART_OPEN_KEY, open ? "1" : "0");
    } catch { }
  };

  // フリープランは表でぼかしている日を出さない（直近7日のみ）
  const promoChartData: PromotionCostBreakdownYoYRow[] = useMemo(() => {
    const pick = (v: unknown) => Number(v ?? 0) || 0;
    return rows
      .filter((r) => !!r.report_date && isFreeRowAllowed(r))
      .map((r) => ({
        month: r.report_date as string, // "YYYY/M/D"
        point_prev: 0,
        mail_prev: 0,
        affiliate_prev: 0,
        ads_prev: 0,
        point_cur: pick(r.point_amount_spent),
        mail_cur: pick(r.mail_amount_spent),
        affiliate_cur: pick(r.afl_amount_spent),
        ads_cur: pick(r.total_promotion),
      }));
  }, [rows, isFreeRowAllowed]);

  const promoChartTitle = isAmazonMall
    ? "販促費内訳（ポイント・広告費）"
    : isYahooMall
      ? "販促費内訳（販促・広告費）"
      : "販促費内訳（ポイント・メルマガ・アフィリ・広告費）";

  // =========================
  // 列表示設定を localStorage から読み込み
  // =========================
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, boolean>;
        setVisibleCols(parsed);
      } else {
        const initial: Record<string, boolean> = {};
        COLUMN_DEFS.forEach((c) => {
          initial[c.key as string] = true;
        });
        setVisibleCols(initial);
      }
    } catch (e) {
      console.error("Failed to load visibleCols from localStorage", e);
    }
  }, []);

  const isVisible = (key: MetricKey) => {
    const v = visibleCols[key as string];
    return v !== false;
  };

  const toggleColumn = (key: MetricKey) => {
    setVisibleCols((prev) => {
      const next = { ...prev, [key as string]: !(prev[key as string] ?? true) };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const setAllColumns = (value: boolean) => {
    const next: Record<string, boolean> = {};
    COLUMN_DEFS.forEach((c) => {
      next[c.key as string] = value;
    });
    setVisibleCols(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  // 楽天外広告費の科目名をデータから抽出（スロット番号→科目名）
  const adNameMap = useMemo(() => {
    const firstRow = rows[0] ?? totalRow;
    const map: Record<string, string> = {};
    for (let i = 1; i <= 3; i++) {
      const name = (firstRow as any)?.[`non_rakuten_ad_${i}_name`];
      if (name) map[`non_rakuten_ad_${i}`] = String(name);
    }
    return map;
  }, [rows, totalRow]);

  const availableColumnDefs = useMemo(
    () =>
      COLUMN_DEFS.filter((c) => {
        const key = c.key as MetricKey;
        // Yahoo は広告内訳も請求明細集計のため楽天専用列をすべて隠す
        if (isYahooMall) return !RAKUTEN_ONLY_KEYS.has(key);
        if (!isAmazonMall) return true;
        // Amazon でもスポンサー広告の内訳は表示する（他の楽天専用列は隠す）
        if (AMAZON_VISIBLE_AD_KEYS.has(key)) return true;
        return !RAKUTEN_ONLY_KEYS.has(key);
      }),
    [isAmazonMall, isYahooMall]
  );


  // 表示対象の列だけ（楽天外広告費はデータがある科目のみ）
  const visibleDefs = useMemo(
    () => availableColumnDefs.filter((c) => {
      const key = c.key as string;
      const adMatch = key.match(/^(non_rakuten_ad_\d+)_/);
      if (adMatch) return !!adNameMap[adMatch[1]];
      return isVisible(c.key as MetricKey);
    }),
    [availableColumnDefs, visibleCols, adNameMap]
  );

  // 列タイトルを返す（楽天外広告費は科目名に上書き）
  const getColTitle = (c: (typeof COLUMN_DEFS)[number]) => {
    const key = c.key as string;
    const match = key.match(/^(non_rakuten_ad_\d+)_cost$/);
    if (match && adNameMap[match[1]]) return adNameMap[match[1]];
    // Amazon/Yahoo では total_promotion に広告費が入るため列名を実態に合わせる
    if ((isAmazonMall || isYahooMall) && key === "total_promotion") return "広告費合計";
    if (isAmazonMall) {
      const amazonTitle = AMAZON_AD_BREAKDOWN_TITLES[key as MetricKey];
      if (amazonTitle) return amazonTitle;
    }
    return c.title;
  };

  // ヘッダ1行目・2行目で楽天外広告費の名前を科目名に上書き
  const resolveAdName = (label: string) => {
    // Amazon の内訳はスポンサー広告なので、楽天前提の見出しを読み替える
    if (isAmazonMall && label === "楽天広告費内訳") return "広告費内訳";
    const numMap: Record<string, number> = { "①": 1, "②": 2, "③": 3 };
    const match = label.match(/^楽天外広告([①②③])$/);
    if (match) {
      const name = adNameMap[`non_rakuten_ad_${numMap[match[1]]}`];
      return name || label;
    }
    return label;
  };

  const getSecTop = (top: string) => resolveAdName(top);

  // -----------------------------
  // ヘッダ段組み（表示列に応じて colSpan 動的計算）
  // -----------------------------
  const countVisibleByGroups = (groups: readonly string[]) =>
    visibleDefs.filter((c) => groups.includes(c.group)).length;

  const headerComputed = useMemo(() => {
    const top = (HEADER_STRUCTURE as readonly HeaderSectionDef[])
      .map((sec) => {
        const colSpan = hasSub(sec)
          ? sec.sub.reduce((sum, s) => sum + countVisibleByGroups(s.groups), 0)
          : countVisibleByGroups(sec.groups);

        return { sec, colSpan };
      })
      .filter((x) => x.colSpan > 0);

    const sub = top.flatMap(({ sec }) => {
      if (!hasSub(sec)) return [];
      return sec.sub
        .map((s) => ({
          top: sec.top,
          name: s.name,
          colSpan: countVisibleByGroups(s.groups),
        }))
        .filter((s) => s.colSpan > 0);
    });

    const tableColCount = 2 + visibleDefs.length;
    return { top, sub, tableColCount };
  }, [visibleDefs]);

  // -----------------------------
  // ✅ groups（設定UI用）
  // -----------------------------
  const groups = useMemo(() => {
    return Array.from(new Set(availableColumnDefs.map((c) => c.group))).map((g) => ({
      name: g,
      cols: availableColumnDefs.filter((c) => c.group === g),
    }));
  }, [availableColumnDefs]);

  // -----------------------------
  // ✅ monthOptions（disabled付き）
  // -----------------------------
  const monthOptions = useMemo(() => {
    const base = dayjs().startOf("month");
    const list: { value: string; label: string; disabled?: boolean }[] = [];

    for (let i = 0; i < 24; i++) {
      const m = base.subtract(i, "month");
      const value = m.format("YYYY-MM");
      const label = m.format("YYYY年MM月");
      const disabled = isFreePlan ? !isFreeMonthAllowed(value) : false;
      list.push({ value, label, disabled });
    }
    return list;
  }, [isFreePlan, isFreeMonthAllowed]);

  // =========================
  // データ取得
  // =========================
  useEffect(() => {
    let canceled = false;

    async function load() {
      try {
        setLoading(true);

        if (isFreePlan && !isFreeMonthAllowed(targetMonth)) {
          const fallbackYm = freeWindow?.end.format("YYYY-MM") ?? initMonth;
          setTargetMonth(fallbackYm);
          if (!canceled) setLoading(false);
          return;
        }

        const res = await apiGet(`/api/dashboard/daily?ym=${targetMonth}`);
        const json = await res.json();

        const data = (json.data || []) as any[];

        const totalRowData = data.find((r) => r.report_date === null);
        const nextTotalRow = totalRowData
          ? ({
            label: dayjs(targetMonth + "-01").format("YYYY年MM月") + "合計",
            ...totalRowData,
          } as DailyRow)
          : null;

        const normalRows = data
          .filter((r) => r.report_date !== null)
          .map((r) => {
            let formattedDate = "";
            if (r.report_date) {
              if (r.report_date.value) {
                formattedDate = dayjs(r.report_date.value).format("YYYY/M/D");
              } else if (typeof r.report_date === "string") {
                formattedDate = dayjs(r.report_date).format("YYYY/M/D");
              }
            }

            return {
              ...r,
              report_date: formattedDate,
              weekday_jp: r.weekday_jp || "",
              weekday_jp_ly: r.weekday_jp_ly || "",
            } as DailyRow;
          });

        if (canceled) return;
        setTotalRow(nextTotalRow);
        setRows(normalRows);
      } catch (e) {
        console.error("DailyProfit load error:", e);
        if (canceled) return;
        setRows([]);
        setTotalRow(null);
      } finally {
        if (!canceled) setLoading(false);
      }
    }

    load();
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMonth, isFreePlan, isFreeMonthAllowed, freeWindow]);

  // -----------------------------
  // CSVエクスポート
  // -----------------------------
  const onExportCsv = React.useCallback(() => {
    const cols: CsvCol<DailyRow>[] = [
      { key: "report_date", header: "日付", type: "text" },
      { key: "weekday_jp", header: "曜", type: "text" },
      ...visibleDefs.map((c) => ({
        key: c.key,
        header: `${c.group}_${getColTitle(c)}`,
        type: (c.type === "rate" ? "rate" : "num") as "rate" | "num",
      })),
    ];

    const baseRows = isFreePlan ? rows.filter(isFreeRowAllowed) : rows;

    const exportRows: DailyRow[] = [];
    if (!isFreePlan && totalRow) {
      exportRows.push({
        ...totalRow,
        report_date: totalRow.label ?? "合計",
        weekday_jp: "",
      });
    }
    exportRows.push(...baseRows);

    downloadCsv(`daily_profit_${targetMonth}.csv`, exportRows, cols, {
      withBom: true,
      newline: "\r\n",
      rateMode: "0-1",
      rateDigits: 1,
    });
  }, [rows, totalRow, visibleDefs, targetMonth, isFreePlan, isFreeRowAllowed]);

  // =========================
  // ✅ 縦スクロール + ヘッダ固定（高さを実測してCSS変数へ）
  // =========================
  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const head1Ref = React.useRef<HTMLTableRowElement | null>(null);
  const head2Ref = React.useRef<HTMLTableRowElement | null>(null);

  React.useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const apply = () => {
      const h1 = head1Ref.current?.offsetHeight ?? 0;
      const h2 = head2Ref.current?.offsetHeight ?? 0;
      el.style.setProperty("--dp-head1", `${h1}px`);
      el.style.setProperty("--dp-head2", `${h2}px`);
    };

    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, [loading, visibleDefs.length, targetMonth]);

  // ✅ 初回ロード中だけ「データなし」を出さない
  const initialLoading = loading && rows.length === 0 && !totalRow;

  const showTotalRow = !!(totalRow && !isFreePlan);
  const totalIsLast = showTotalRow && rows.length === 0;
  const titleLabel =
    mall === "amazon"
      ? "Amazon日次損益計算表"
      : mall === "yahoo"
        ? "Yahoo!ショッピング日次損益計算表"
        : "楽天日次損益計算表";

  return (
    <div className="w-pl flex flex-col relative">
      {/* ✅ 画面は描画して、loading中だけ overlay スピナー */}
      {loading && (
        <div className="fixed top-14 bottom-0 left-0 md:left-[210px] right-0 z-50 flex items-center justify-center bg-white/0">
          <LoadingInline />
        </div>
      )}

      {/* ▼ヘッダー（月選択＋列表示設定） */}
      <div className="flex items-start mb-4 gap-3">
        {/* 左：列表示 + フリープラン文言 */}
        <div className="flex flex-col items-start shrink-0">
          <details className="self-start border rounded-md bg-white p-2 text-sm">
            <summary className="cursor-pointer select-none text-blue-600">
              列表示設定
            </summary>

            <div className="mt-2 flex flex-col gap-2">
              <div className="flex gap-2">
                <button
                  className="text-xs border rounded px-2 py-1 bg-white hover:bg-slate-50 disabled:opacity-50"
                  type="button"
                  onClick={() => setAllColumns(true)}
                  disabled={loading}
                >
                  全部ON
                </button>
                <button
                  className="text-xs border rounded px-2 py-1 bg-white hover:bg-slate-50 disabled:opacity-50"
                  type="button"
                  onClick={() => setAllColumns(false)}
                  disabled={loading}
                >
                  全部OFF
                </button>
              </div>

              <div className="max-h-64 overflow-y-auto pr-1">
                {groups.map((g) => (
                  <div key={g.name} className="mb-2">
                    <div className="font-semibold text-xs text-slate-700 mb-1">
                      {g.name}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {g.cols.map((c) => {
                        const key = c.key as MetricKey;
                        return (
                          <label
                            key={c.key as string}
                            className="flex items-center gap-1 text-xs border rounded px-1 py-0.5 bg-slate-50"
                          >
                            <input
                              type="checkbox"
                              checked={isVisible(key)}
                              onChange={() => toggleColumn(key)}
                              disabled={loading}
                            />
                            <span>{getColTitle(c)}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </details>

          {isFreePlan && (
            <p className="mt-2 text-xs text-slate-500">
              フリープランは直近７日間のみ表示されます。全データの閲覧は
              <a
                href={UPGRADE_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800"
                onClick={() => notifyUpgradeInterest("日次利益（全期間表示）")}
              >
                プランをアップグレード
              </a>
              してください。
            </p>
          )}
        </div>

        {/* 右：CSV + 年月（右寄せ） */}
        <div className="ml-auto flex items-end gap-2">
          <button
            type="button"
            className="text-sm border rounded px-2 py-1 bg-white hover:bg-slate-50 disabled:opacity-50"
            onClick={onExportCsv}
            disabled={loading}
          >
            CSVエクスポート
          </button>

          <div className="flex flex-col items-end">
            <select
              value={targetMonth}
              onChange={(e) => {
                const next = e.target.value;
                if (isFreePlan && !isFreeMonthAllowed(next)) {
                  showPlanBlocked();
                  return;
                }
                setTargetMonth(next);
              }}
              className="border rounded px-2 py-1 text-sm disabled:opacity-50"
              disabled={loading}
            >
              {monthOptions.map((m) => (
                <option key={m.value} value={m.value} disabled={m.disabled}>
                  {m.label}
                </option>
              ))}
            </select>

            {planBlockMsg && (
              <div className="mt-1 text-xs text-red-600">{planBlockMsg}</div>
            )}

            {isFreePlan && freeWindow && (
              <div className="mt-1 text-[11px] text-slate-600">
                フリープラン：
                {freeWindow.start.format("YYYY/M/D")}〜
                {freeWindow.end.format("YYYY/M/D")} のみ表示
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ▼販促費内訳グラフ（初期は閉じる。閉じている間はグラフを描かない） */}
      <details
        className={`border rounded-md bg-white p-2 text-sm ${promoChartOpen ? "" : "self-start"}`}
        open={promoChartOpen}
        onToggle={(e) => {
          const open = (e.currentTarget as HTMLDetailsElement).open;
          if (open !== promoChartOpen) onTogglePromoChart(open);
        }}
      >
        <summary className="cursor-pointer select-none text-blue-600">
          販促費内訳グラフ
        </summary>

        {promoChartOpen && (
          <PromotionCostBreakdownYoYChart
            data={promoChartData}
            loading={loading}
            emptyLabel={initialLoading ? "読み込み中..." : "データがありません"}
            title={promoChartTitle}
            frameless
            autoBarSize
            formatTick={(m) => {
              const d = dayjs(m);
              return d.isValid() ? d.format("M/D") : m;
            }}
            formatTooltipTitle={(m) => {
              const d = dayjs(m);
              return d.isValid() ? `${m}（${WEEKDAY_JP[d.day()]}）` : m;
            }}
          />
        )}
      </details>

      <p className="mt-4 mb-1 text-[11px] text-slate-400">
        ← 横にスクロールすると他の指標を表示できます →
        <span className="ml-3">
          <span className="dp-legend-good">■</span> 営業利益 上位3日
          <span className="dp-legend-bad ml-2">■</span> 営業利益 赤字下位3日
        </span>
      </p>

      {/* ✅ 縦スクロール対象 */}
      <div ref={wrapRef} className="dp-table-wrapper dp-table-vert">
        <table
          className="dp-table"
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
        >
          <thead>
            {/* 1行目：大分類 */}
            <tr ref={head1Ref} className="dp-headrow-1">
              <th className="dp-title-cell dp-title-sticky" rowSpan={2} colSpan={2}>
                <div>{titleLabel}</div>
                <div className="dp-title-sub">
                  {dayjs(targetMonth + "-01").format("YYYY-MM")}
                </div>
              </th>

              {headerComputed.top.map(({ sec, colSpan }) => (
                <th
                  key={sec.top}
                  colSpan={colSpan}
                  rowSpan={isRowSpan2(sec) ? 2 : 1}
                  className="dp-group-header"
                >
                  {getSecTop(sec.top)}
                </th>
              ))}
            </tr>

            {/* 2行目：中分類 */}
            <tr ref={head2Ref} className="dp-headrow-2">
              {headerComputed.sub.map((s) => (
                <th
                  key={`${s.top}-${s.name}`}
                  colSpan={s.colSpan}
                  className="dp-subgroup-header"
                >
                  {resolveAdName(s.name)}
                </th>
              ))}
            </tr>

            {/* 3行目：個別列 */}
            <tr className="dp-headrow-3">
              <th className="dp-col-header sticky-left-1">日付</th>
              <th className="dp-col-header sticky-left-2">曜</th>

              {visibleDefs.map((c) => (
                <th key={c.key as string} className="dp-col-header">
                  {getColTitle(c)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {/* ✅ 初回ロード中：データなしを出さない（overlayが出るのでこれでOK） */}
            {initialLoading && (
              <tr>
                <td
                  colSpan={headerComputed.tableColCount}
                  className="p-4 text-center text-slate-500"
                >
                  読み込み中...
                </td>
              </tr>
            )}

            {/* ✅ 合計行：フリープランは非表示 */}
            {!initialLoading && totalRow && !isFreePlan && (
              <tr className="dp-total-row">
                <td className="dp-cell sticky-left-1 dp-date-cell" colSpan={2}>
                  {totalRow.label}
                </td>

                {visibleDefs.map((c) => {
                  const raw = totalRow[c.key] as number | null | undefined;
                  const content = fmtCell(c.key as MetricKey, c.type, raw);

                  const extraClass = c.type === "rate" ? "rate" : "num";
                  const negClass = isNegative(raw) ? "dp-neg" : "";

                  return (
                    <td
                      key={c.key as string}
                      className={`dp-cell ${extraClass} ${negClass}`}
                    >
                      {content}
                    </td>
                  );
                })}
              </tr>
            )}

            {!initialLoading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={headerComputed.tableColCount}
                  className="p-4 text-center text-slate-500"
                >
                  データなし
                </td>
              </tr>
            )}

            {/* 通常行 */}
            {!initialLoading &&
              rows.map((r, i) => {
                const weekdayClass =
                  r.weekday_jp === "土"
                    ? "dp-week-sat"
                    : r.weekday_jp === "日"
                      ? "dp-week-sun"
                      : "";

                const locked = isFreePlan && !isFreeRowAllowed(r);

                const dateStr = r.report_date || "";
                // 楽天イベント（SS/マラソン等）を優先し、なければ「5と0のつく日」
                const dayEvent = isRakutenMall ? eventOfDate(dateStr) : null;
                const dayNum = Number(dateStr.split("/")[2]);
                const isFiveZeroDay =
                  isRakutenMall &&
                  !dayEvent &&
                  Number.isFinite(dayNum) &&
                  dayNum > 0 &&
                  dayNum % 5 === 0;
                const isGoodDay = !locked && opHighlight.good.has(dateStr);
                const isBadDay = !locked && opHighlight.bad.has(dateStr);
                const dayClass = isGoodDay
                  ? "dp-date-good"
                  : isBadDay
                    ? "dp-date-bad"
                    : "";

                return (
                  <tr
                    key={i}
                    className={locked ? "dp-row-locked" : undefined}
                    title={
                      locked
                        ? "フリープランでは直近7日分のみ閲覧できます（アップグレードで全期間表示）"
                        : undefined
                    }
                  >
                    <td className={`dp-cell sticky-left-1 dp-date-cell ${dayClass}`}>
                      {r.report_date || ""}
                      {dayEvent && (
                        <span
                          className={`dp-sale-badge ${dayEvent.type === "SS"
                            ? "dp-evt-ss"
                            : dayEvent.type === "MA"
                              ? "dp-evt-ma"
                              : "dp-evt-other"
                            }`}
                          title={`${dayEvent.label}（${dayEvent.startDate}〜${dayEvent.endDate}）`}
                        >
                          {dayEvent.type === "SS"
                            ? "SS"
                            : dayEvent.type === "MA"
                              ? "マ"
                              : (dayEvent.label || "イベント").slice(0, 3)}
                        </span>
                      )}
                      {isFiveZeroDay && (
                        <span
                          className="dp-sale-badge"
                          title="楽天 5と0のつく日"
                        >
                          5と0
                        </span>
                      )}
                    </td>
                    <td
                      className={`dp-cell sticky-left-2 dp-week-cell ${weekdayClass}`}
                    >
                      {r.weekday_jp || ""}
                    </td>

                    {visibleDefs.map((c) => {
                      const raw = r[c.key] as number | null | undefined;
                      const content = fmtCell(c.key as MetricKey, c.type, raw);

                      const extraClass = c.type === "rate" ? "rate" : "num";
                      const negClass = isNegative(raw) ? "dp-neg" : "";
                      const opClass =
                        c.key === "operating_profit"
                          ? isGoodDay
                            ? "dp-op-good"
                            : isBadDay
                              ? "dp-op-bad"
                              : ""
                          : "";

                      return (
                        <td
                          key={c.key as string}
                          className={`dp-cell ${extraClass} ${negClass} ${opClass} ${locked ? "dp-blur-cell" : ""
                            }`}
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {isRakutenMall && (
        <p className="mt-1 text-[11px] text-slate-500">
          ※物流費が「ー」の場合はRMSの数字が更新されておりません。お時間をおいてアクセスしてください。
        </p>
      )}
    </div>
  );
};

export default DailyProfitPage;
